class CartRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.setupEventListeners();
  }

  disconnectedCallback() {
    if (this._controller) {
      this._controller.abort();
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
  }

  setupEventListeners() {
    if (this._controller) {
      this._controller.abort();
    }
    this._controller = new AbortController();

    if (!this._boundAddHandler) {
      this._boundAddHandler = this.handleAddToCart.bind(this);
    }

    this.grid = this.querySelector('[data-recommendations-grid]');
    this.prevBtn = this.querySelector('.cart-recommendations__nav--prev');
    this.nextBtn = this.querySelector('.cart-recommendations__nav--next');

    this.querySelectorAll('[data-add-to-cart]').forEach((button) => {
      if (button instanceof HTMLElement) {
        button.addEventListener('click', this._boundAddHandler, { signal: this._controller?.signal });
      }
    });

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.scrollByAmount(-this.getScrollAmount());
      }, { signal: this._controller.signal });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.scrollByAmount(this.getScrollAmount());
      }, { signal: this._controller.signal });
    }

    if (this.grid) {
      this.grid.addEventListener('scroll', () => this.updateNav(), { signal: this._controller.signal });
    }

    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
    if (this.grid) {
      this._resizeObserver = new ResizeObserver(() => this.updateNav());
      this._resizeObserver.observe(this.grid);
    }

    this.updateNav();
  }

  getScrollAmount() {
    if (!this.grid) return 0;
    return this.grid.clientWidth;
  }

  /**
   * @param {number} amount
   */
  scrollByAmount(amount) {
    if (!this.grid) return;
    this.grid.scrollBy({ left: amount, behavior: 'smooth' });
  }

  updateNav() {
    if (!this.grid || !this.prevBtn || !this.nextBtn) return;
    const maxScroll = this.grid.scrollWidth - this.grid.clientWidth;
    const left = Math.round(this.grid.scrollLeft);
    if (this.prevBtn instanceof HTMLButtonElement) {
      this.prevBtn.disabled = left <= 0;
    }
    if (this.nextBtn instanceof HTMLButtonElement) {
      this.nextBtn.disabled = left >= Math.round(maxScroll - 1);
    }
  }

  /**
   * @param {Event} event
   */
  async handleAddToCart(event) {
    event.preventDefault();
    event.stopPropagation();

    const currentTarget = event.currentTarget;
    if (!(currentTarget instanceof HTMLButtonElement)) return;
    const button = currentTarget;

    /** @type {HTMLElement | null} */
    let item = button.closest('.cart-recommendations__item');

    const variantId = button.dataset?.variantId;
    if (!variantId) return;

    button.disabled = true;
    if (item) {
      item.setAttribute('data-adding', '');
    }

    try {
      const sectionId = this.getSectionId();
      /** @type {any} */
      const requestBody = {
        items: [{
          id: variantId,
          quantity: 1
        }]
      };

      if (sectionId) {
        requestBody.sections = [sectionId];
      }
      requestBody.sections_url = window.location.pathname + window.location.search;

      const shopifyRoutes = /** @type {any} */ (window.Shopify);
      const response = await fetch(shopifyRoutes.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || 'Could not add to cart');
      }

      const result = await response.json();

      if (item) {
        item.remove();
      }
      this.reindexGrid();

      if (result.sections) {
        this.updateCartSections(result.sections);
      }

      document.dispatchEvent(new CustomEvent('cart:refresh', {
        detail: { 
          cart: result,
          sections: result.sections 
        }
      }));
    } catch (error) {
      if (item) {
        item.removeAttribute('data-adding');
      }
      if (button instanceof HTMLButtonElement) {
        button.disabled = false;
      }
    }
  }

  getSectionId() {
    const cartDrawer = document.querySelector('cart-items-component');
    if (cartDrawer instanceof HTMLElement && cartDrawer.dataset?.sectionId) {
      return cartDrawer.dataset.sectionId;
    }
    return null;
  }

  /**
   * @param {Record<string, string>} sections
   */
  updateCartSections(sections) {
    if (!sections) return;

    for (const [sectionId, sectionHtml] of Object.entries(sections)) {
      const targetElement = document.querySelector(`[data-section-id="${sectionId}"]`);
      if (targetElement && sectionHtml) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionHtml, 'text/html');
        const newContent = doc.querySelector(`[data-section-id="${sectionId}"]`);
        
        if (newContent) {
          targetElement.innerHTML = newContent.innerHTML;
        }
      }
    }
  }

  reindexGrid() {
    const grid = this.querySelector('[data-recommendations-grid]');
    const items = grid ? grid.querySelectorAll('.cart-recommendations__item') : [];
    
    if (items.length === 0) {
      this.style.display = 'none';
    }
    this.updateNav();
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
